import { useState, type ChangeEvent } from "react";
import { useTranslation } from "react-i18next";
import {
  Calendar,
  Clock,
  MapPin,
  Phone,
  Download,
  ExternalLink,
  User,
  FileText,
  X,
  CalendarPlus,
  Video,
} from "lucide-react";
import { ConfirmDialog, DialogHeader, DialogTitle, Input, Label, Separator, cn } from "@tellescope/ui";
import { Badge, Button } from "@tellescope/design-system";
import { formatTime, formatName, ensureHttps, buildGoogleCalendarUrl } from "@tellescope/utils";
import { useUsersMap } from "@tellescope/tanstack-query";
import type { CalendarEvent } from "@tellescope/types-models";
import { useEnduserStore } from "../../store";
import { useCancelCalendarEvent } from "../../hooks/useCancelCalendarEvent";
import { useJoinVideoCall } from "../../hooks/useJoinVideoCall";
import { detectVideoIntegration } from "../../../shared/utils/detectVideoIntegration";
import { useEventStatus } from "../../hooks/useEventStatus";
import { useTemplateContext } from "../../hooks/useTemplateContext";
import { getCancelGate, getRescheduleGate } from "../../utils/appointmentActionGates";
import { interpolateText } from "@tellescope/utils";
import { ASYNC_SOURCES } from "../../../shared/template";
import { VideoCallButton } from "../VideoCallButton";
import { getApiURL } from "../../../config/api";

interface AppointmentDetailsContentProps {
  event: CalendarEvent;
  onOpenChange: (open: boolean) => void;
  allowCancel: boolean;
  allowReschedule?: boolean;
  rescheduleUrl?: string;
  hideVideoAfterMinutes?: number;
  onRescheduleClick?: (event: CalendarEvent) => void;
}

export const AppointmentDetailsContent = ({
  event,
  onOpenChange,
  allowCancel,
  allowReschedule,
  rescheduleUrl,
  hideVideoAfterMinutes,
  onRescheduleClick,
}: AppointmentDetailsContentProps) => {
  const { t } = useTranslation(["events", "videoCalling"]);
  const session = useEnduserStore((state) => state.session);
  const currentEnduser = useEnduserStore((state) => state.currentEnduser);
  const templateContext = useTemplateContext();

  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const [cancelReason, setCancelReason] = useState("");

  const { cancelEventAsync, isPending: isCancelling } = useCancelCalendarEvent();
  const { joinVideoCall } = useJoinVideoCall();

  const providerIds = event.attendees.reduce<string[]>((acc, a) => {
    if (a.type === "user" && !acc.includes(a.id)) acc.push(a.id);
    return acc;
  }, []);
  const usersMap = useUsersMap(providerIds.length > 0 ? providerIds : undefined);

  const { isCancelled, isRescheduled, isUpcoming, canJoinVideo, isVideoStartingSoon } = useEventStatus(
    event,
    currentEnduser
  );
  const hostUserUrl = event.useUserURL && providerIds[0] ? usersMap[providerIds[0]]?.url : undefined;
  const videoIntegration = detectVideoIntegration(event, hostUserUrl);

  const eventEndTimeInMS = event.startTimeInMS + event.durationInMinutes * 60 * 1000;
  const isVideoHidden =
    hideVideoAfterMinutes !== undefined && Date.now() > eventEndTimeInMS + hideVideoAfterMinutes * 60 * 1000;

  const interpolatedRescheduleUrl = rescheduleUrl
    ? interpolateText(rescheduleUrl, templateContext, { deferredSources: ASYNC_SOURCES })
    : undefined;

  const handleDownloadIcs = () => {
    if (!session) return;
    const icsUrl = `${getApiURL()}/v1/calendar-events/download-ics-file?authToken=${
      session.authToken
    }&calendarEventId=${event.id}`;
    window.location.href = icsUrl;
  };

  const handleAddToGoogleCalendar = () => {
    window.open(buildGoogleCalendarUrl(event), "_blank");
  };

  const handleConfirmCancel = async () => {
    await cancelEventAsync({
      eventId: event.id,
      cancelReason: cancelReason || undefined,
    });
    setCancelReason("");
    setShowCancelConfirm(false);
    onOpenChange(false);
  };

  const startDate = new Date(event.startTimeInMS);
  const endTime = new Date(event.startTimeInMS + event.durationInMinutes * 60 * 1000);
  const displayTitle = event.displayTitle ?? event.title;

  const isReschedulePrevented = event.preventRescheduleInPortal === true;

  const cancelGate = getCancelGate(event, allowCancel, isUpcoming, isCancelled);
  const showCancelButton = cancelGate.visible;
  // Deliberately separate from getRescheduleGate below — this gates the post-cancellation
  // external link (isCancelled), that gates the in-app action (!isCancelled); they can't overlap.
  const showRescheduleButton = isCancelled && !!rescheduleUrl && !isReschedulePrevented;

  const rescheduleGate = getRescheduleGate(event, allowReschedule, isUpcoming, isCancelled);
  const showRescheduleAction = rescheduleGate.visible;

  const providers = providerIds.reduce<NonNullable<(typeof usersMap)[string]>[]>((acc, id) => {
    const user = usersMap[id];
    if (user != null) acc.push(user);
    return acc;
  }, []);

  const hasDetails =
    (providers.length > 0 && !event.portalSettings?.hideUsers) ||
    !!event.location ||
    !!event.locationURL ||
    !!event.phone ||
    !!event.locationNotes ||
    !!event.displayDescription;

  const hasActions = showCancelButton || showRescheduleButton || showRescheduleAction;

  return (
    <>
      <DialogHeader className="px-5 pt-5 pb-0">
        <div className="flex items-start gap-3">
          <div
            className={cn(
              "w-12 h-12 rounded-lg flex flex-col items-center justify-center flex-shrink-0",
              isCancelled ? "bg-muted" : "bg-primary/10"
            )}
          >
            <Calendar className={cn("h-3.5 w-3.5", isCancelled ? "text-muted-foreground" : "text-primary")} />
            <span
              className={cn(
                "text-xs font-medium leading-none mt-0.5",
                isCancelled ? "text-muted-foreground" : "text-primary"
              )}
            >
              {startDate.toLocaleDateString(undefined, { month: "short" })}
            </span>
            <span
              className={cn(
                "text-sm font-bold leading-none mt-0.5",
                isCancelled ? "text-muted-foreground" : "text-primary"
              )}
            >
              {startDate.getDate()}
            </span>
          </div>
          <div className="flex-1 min-w-0 pt-0.5">
            <div className="flex items-center gap-2 flex-wrap">
              <DialogTitle className="text-base font-semibold">{displayTitle}</DialogTitle>
              {isCancelled && <Badge color="destructive">{t("Cancelled")}</Badge>}
              {isRescheduled && !isCancelled && <Badge color="secondary">{t("Rescheduled")}</Badge>}
            </div>
            <div className="flex items-center gap-1 text-sm text-muted-foreground mt-1">
              <Clock className="h-3.5 w-3.5 flex-shrink-0" />
              <span>
                {startDate.toLocaleDateString(undefined, {
                  weekday: "short",
                  month: "short",
                  day: "numeric",
                })}{" "}
                · {formatTime(event.startTimeInMS)} – {formatTime(endTime.getTime())}
              </span>
            </div>
          </div>
        </div>
      </DialogHeader>

      <div className="px-5 pb-5 space-y-4 overflow-y-auto">
        {/* Calendar actions */}
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={handleDownloadIcs}>
            <Download className="h-3.5 w-3.5 mr-1.5" />
            {t("Download ICS File")}
          </Button>
          <Button variant="outline" size="sm" className="h-8 text-xs" onClick={handleAddToGoogleCalendar}>
            <CalendarPlus className="h-3.5 w-3.5 mr-1.5" />
            {t("Add to Google Calendar")}
          </Button>
        </div>

        {/* Video call button */}
        {videoIntegration.hasAnyVideo && !isCancelled && !isVideoHidden && videoIntegration.videoType && (
          <>
            <Separator />
            <div className="flex items-center gap-2 text-sm text-muted-foreground mb-1">
              <Video className="h-4 w-4" />
              <span className="font-medium">{t("Video Call")}</span>
            </div>
            <VideoCallButton
              event={event}
              videoType={videoIntegration.videoType}
              videoUrl={videoIntegration.primaryVideoUrl}
              size="sm"
              disabled={videoIntegration.videoType === "twilio" && !canJoinVideo}
              onTwilioClick={() => joinVideoCall(event)}
            />
            {videoIntegration.videoType === "twilio" && !canJoinVideo && isVideoStartingSoon && (
              <p className="text-xs text-muted-foreground">
                {t("Waiting for host to start...", { ns: "videoCalling" })}
              </p>
            )}
          </>
        )}

        {/* Event details */}
        {hasDetails && (
          <>
            <Separator />
            <div className="space-y-2.5 text-sm">
              {providers.length > 0 && !event.portalSettings?.hideUsers && (
                <div className="flex items-start gap-2.5 text-muted-foreground">
                  <User className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <div>
                    <span className="font-medium">{providers.length === 1 ? t("Provider") : t("Providers")}</span>
                    <div className="mt-0.5 space-y-0.5">
                      {providers.map((user) => (
                        <p key={user.id} className="text-foreground">
                          {formatName(user)}
                        </p>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {event.location && (
                <div className="flex items-start gap-2.5 text-muted-foreground">
                  <MapPin className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span className="text-foreground">{event.location}</span>
                </div>
              )}

              {event.locationURL && (
                <div className="flex items-start gap-2.5 text-muted-foreground">
                  <ExternalLink className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <a
                    href={ensureHttps(event.locationURL)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary hover:underline break-all"
                  >
                    {event.locationURL}
                  </a>
                </div>
              )}

              {event.phone && (
                <div className="flex items-start gap-2.5 text-muted-foreground">
                  <Phone className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <a href={`tel:${event.phone}`} className="text-primary hover:underline">
                    {event.phone}
                  </a>
                </div>
              )}

              {event.locationNotes && (
                <div className="flex items-start gap-2.5 text-muted-foreground">
                  <FileText className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span className="text-foreground whitespace-pre-wrap">{event.locationNotes}</span>
                </div>
              )}

              {event.displayDescription && (
                <div className="flex items-start gap-2.5 text-muted-foreground">
                  <FileText className="h-4 w-4 mt-0.5 flex-shrink-0" />
                  <span className="text-foreground whitespace-pre-wrap">{event.displayDescription}</span>
                </div>
              )}
            </div>
          </>
        )}

        {/* Actions */}
        {hasActions && (
          <>
            <Separator />
            <div className="space-y-2">
              {showRescheduleButton && interpolatedRescheduleUrl && (
                <a
                  href={interpolatedRescheduleUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center justify-start gap-2 w-full h-9 px-3 text-sm font-medium text-primary border border-input rounded-md hover:bg-accent transition-colors"
                >
                  <CalendarPlus className="h-4 w-4" />
                  {t("Reschedule")}
                </a>
              )}

              {showRescheduleAction && (
                <Button
                  variant="outline"
                  className="w-full justify-start"
                  disabled={rescheduleGate.disabled}
                  onClick={() => onRescheduleClick?.(event)}
                >
                  <CalendarPlus className="h-4 w-4 mr-2" />
                  {t("Reschedule Appointment")}
                </Button>
              )}

              {showCancelButton && (
                <Button
                  variant="ghost"
                  color="destructive"
                  className="w-full justify-start"
                  disabled={cancelGate.disabled}
                  onClick={() => {
                    setCancelReason("");
                    setShowCancelConfirm(true);
                  }}
                >
                  <X className="h-4 w-4 mr-2" />
                  {t("Cancel Appointment")}
                </Button>
              )}
            </div>
          </>
        )}
      </div>

      <ConfirmDialog
        open={showCancelConfirm}
        onOpenChange={(isOpen) => {
          if (!isOpen) setCancelReason("");
          setShowCancelConfirm(isOpen);
        }}
        variant="destructive"
        title={t("Cancel Appointment")}
        description={t("Are you sure you want to cancel this appointment?")}
        confirmText={t("Yes, cancel appointment")}
        cancelText={t("Keep appointment")}
        onConfirm={handleConfirmCancel}
        loading={isCancelling}
        confirmDisabled={!!event.requirePortalCancelReason && !cancelReason.trim()}
        customContent={
          <div className="space-y-1">
            <Label htmlFor="dialogCancelReason" required={event.requirePortalCancelReason === true}>
              {t("Reason for cancellation")}
            </Label>
            <Input
              id="dialogCancelReason"
              value={cancelReason}
              onChange={(e: ChangeEvent<HTMLInputElement>) => setCancelReason(e.target.value)}
              placeholder={t("Please provide a reason")}
            />
          </div>
        }
      />
    </>
  );
};
